import PropTypes from 'prop-types';
import { useRef, useState, useEffect } from 'react';
// @mui
import { Box, Checkbox, TableCell, TableHead, TableRow, TableSortLabel } from '@mui/material';

// ----------------------------------------------------------------------

const visuallyHidden = {
    border: 0,
    margin: -1,
    padding: 0,
    width: '1px',
    height: '1px',
    overflow: 'hidden',
    position: 'absolute',
    whiteSpace: 'nowrap',
    clip: 'rect(0 0 0 0)',
};

// ----------------------------------------------------------------------

TableHeadCustom.propTypes = {
    sx: PropTypes.object,
    onSort: PropTypes.func,
    orderBy: PropTypes.string,
    headLabel: PropTypes.array,
    rowCount: PropTypes.number,
    numSelected: PropTypes.number,
    onSelectAllRows: PropTypes.func,
    order: PropTypes.oneOf(['asc', 'desc']),
};

export default function TableHeadCustom({
    order,
    orderBy,
    rowCount = 0,
    headLabel,
    numSelected = 0,
    onSort,
    onSelectAllRows,
    sx,
}) {
    const [cols, setCols] = useState(() => [...headLabel]);

    useEffect(() => {
        setCols([...headLabel]);
    }, [headLabel, headLabel.length]);

    const handleResize = (index, newWidth) => {
        const newCols = [...cols];
        newCols[index] = { ...newCols[index], width: newWidth };
        setCols(newCols);
    };

    return (
        <TableHead sx={sx}>
            <TableRow>
                {onSelectAllRows && (
                    <TableCell padding="checkbox">
                        <Checkbox
                            indeterminate={numSelected > 0 && numSelected < rowCount}
                            checked={rowCount > 0 && numSelected === rowCount}
                            onChange={(event) => onSelectAllRows(event.target.checked)}
                        />
                    </TableCell>
                )}

                {cols.map((headCell, index) => (
                    <ResizableTableCell
                        key={`${index}-${headCell.label}`}
                        headCell={headCell}
                        order={order}
                        orderBy={orderBy}
                        onSort={onSort}
                        onResize={(width) => handleResize(index, width)}
                    />
                ))}
            </TableRow>
        </TableHead>
    );
}

// ----------------------------------------------------------------------

function ResizableTableCell({ headCell, order, orderBy, onSort, onResize }) {
    const thRef = useRef(null);
    const isResizing = useRef(false);

    const handleMouseDown = (e) => {
        e.preventDefault();
        e.stopPropagation();
        isResizing.current = true;

        // Mora se uzeti width roditeljskog th elementa
        const parentCell = thRef.current.closest('th');
        if (!parentCell) return;

        const startX = e.pageX;
        const startWidth = parentCell.offsetWidth;

        const handleMouseMove = (e) => {
            if (isResizing.current) {
                const newWidth = startWidth + (e.pageX - startX);
                if (newWidth > 50) {
                    onResize(newWidth);
                }
            }
        };

        const handleMouseUp = () => {
            isResizing.current = false;
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = 'default';
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = 'col-resize';
    };

    return (
        <TableCell
            align={headCell.align || 'left'}
            sortDirection={orderBy === headCell.id ? order : false}
            sx={{
                width: headCell.width,
                minWidth: headCell.minWidth,
                borderRight: "1px solid rgba(224, 224, 224, 1)",
                '&:last-of-type': { borderRight: 'none' },
                backgroundColor: 'background.paper',
            }}
        >

            <Box
                ref={thRef}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    position: 'relative',
                    width: '100%',
                    height: '100%'
                }}
            >
                {canSort(onSort, headCell) ? (
                    <TableSortLabel
                        hideSortIcon
                        active={orderBy === headCell.id}
                        direction={orderBy === headCell.id ? order : 'asc'}
                        onClick={() => onSort(headCell.id)}
                        sx={{ flex: 1 }}
                    >
                        {headCell.label}
                        {orderBy === headCell.id ? (
                            <Box sx={{ ...visuallyHidden }}>
                                {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                            </Box>
                        ) : null}
                    </TableSortLabel>
                ) : (
                    <Box sx={{ flex: 1 }}>{headCell.label}</Box>
                )}


                <Box
                    onMouseDown={handleMouseDown}
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                        position: 'absolute',
                        right: -16,
                        top: 0,
                        width: '20px',
                        height: '100%',
                        cursor: 'col-resize',
                        zIndex: 1,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        opacity: 0,
                        '&:hover': {
                            opacity: 1,
                            borderRight: '2px solid',
                            borderColor: 'primary.main'
                        }
                    }}
                />
            </Box>
        </TableCell>
    );
}

const canSort = (onSort, headCell) => {
    if (!onSort) {
        return false;
    }
    if (headCell.sortable === undefined || headCell.sortable === null) {
        return true
    }
    return headCell.sortable
}
